import React, { useState, useCallback, useMemo } from 'react';
import './dropdown-button.css';
import DropdownMenuPopup from './DropdownMenuPopup';
import { IDropdownMenuCardOption } from './DropdownMenuCard';
import { onClickAway } from '../../utils/DOM';

interface IDropdownButtonProps<Value> {
	value: Value;
	options: IDropdownMenuCardOption[];
	id?: string;
	popupId?: string;
	clickAwayTarget?: Element;
}

export default function DropdownButton<Value extends string = string>({
	value,
	options,
	id: propsId,
	popupId: propsPopupId,
	clickAwayTarget,
}: IDropdownButtonProps<Value>) {
	const [isOpen, setIsOpen] = useState(false);

	// Fall back to a random id if none is provided
	const id = useMemo(
		() => propsId || Math.random().toString(36).substring(2, 9),
		[propsId]
	);
	const popupId = useMemo(
		() => propsPopupId || `${id}-popup`,
		[propsPopupId, id]
	);

	const onClickHandler = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.stopPropagation();

			if (isOpen) {
				setIsOpen(false);
				return;
			}

			const popupElement = document.getElementById(popupId);

			onClickAway(
				popupElement,
				() => {
					setIsOpen(false);
				},
				clickAwayTarget
			);

			setIsOpen(true);
		},
		[isOpen, popupId, clickAwayTarget]
	);

	const selectedOption = useMemo(
		() => options.find((o) => o.value === value) || options[0],
		[options, value]
	);

	return (
		<div className='ds-dropdown-button'>
			<button
				className='ds-dropdown-button__button'
				id={id}
				onClick={onClickHandler}
			>
				<div style={{ display: 'flex', gap: 10 }}>
					{selectedOption?.iconEnd || selectedOption?.iconStart ? (
						<i
							className={`fa-solid fa-${
								selectedOption.iconEnd || selectedOption.iconStart
							}`}
						/>
					) : null}
					<span>{selectedOption?.label}</span>
				</div>
				<i className='fa-solid fa-caret-down' />
			</button>
			<DropdownMenuPopup<Value>
				popupOptions={{
					anchorQuerySelector: `#${id}`,
					placement: 'bottom',
					noArrow: true,
					open: isOpen,
					id: popupId,
				}}
				value={value}
				// @ts-ignore
				options={options.map((option) => ({
					...option,
					onClick: option.onClick
						? (value, e) => {
								setIsOpen(false);
								option.onClick?.(value as Value, e);
						  }
						: undefined,
				}))}
			/>
		</div>
	);
}
